/**
 * Browser entry point. Two gates before the app loads:
 *   1. Authentication (auth.ui) — local dev token or Privy session.
 *   2. Agent selection (agents.ui) — pick/create which agent to use.
 * Then it patches fetch to thread the agent id and dynamically imports the app
 * (main.ts opens the agent WebSocket on import, so it must run last).
 */
import { assumeSignedIn, captureToken, ensureAuthenticated, showComputerLanding, stillSignedIn } from "./auth.ui.ts";
import { forgetKeptAnswers, readOpenState, writeOpenState } from "./openState.ts";
import { lastAgent, rememberLastAgent, startEarlyReads, startFirstOpenRead } from "./earlyReads.ts";
import { linkPageFirst } from "./linkFirst.ts";
import { agentStillOurs, askAgentsEarly, chosenIsNamed, ensureAgentSelected, installAgentHeader, installAgentCard } from "./agents.ui.ts";
import { applyLangAttr, ensureLangWords, getLang, hydrateLangFromServer, registerWordsLoader, reportUserZone, seedUserLangDefault } from "./i18n.ts";
import { loadPluginWords } from "../../plugins/nmo/web/words.ts";
import { showWelcome, finishWelcome } from "./welcome.ts";
import { checkAtStart, installStaleChunkRecovery, startVersionWatcher } from "./auto-update.ts";
import { clearStaleReloadGuard } from "./island-util.ts";
import { serverIsKnownAway, waitForServer, watchForTheServerGoingAway } from "./server-wait.ts";
import { captureInstallPrompt } from "./installPrompt.ts";
import { stashSharedTrader } from "./sharedTraderLink.ts";
import { adminAsked, desktopClosed } from "./phoneGate.ts";
import { installSharedReads } from "./sharedReads.ts";
import { installPhoneStore } from "./phoneStore.ts";
import { installBatchedReads } from "./batchedReads.ts";
import { installLaunchReads } from "./launchReads.ts";
import { installLoadTimes } from "./loadTimes.ts";
import { clearDoor, firstRunDueHere, runFirstRunBeforeApp } from "./firstRunSequence.ts";
import { pointHomeIconAt } from "./homeIcon.ts";
import { holdSplashForFacts, splashAt, splashLoaded, startWasUnreachable } from "./startSplash.ts";

declare const __GENIE_NMO__: boolean;

/** #1950: under the shared-read join, so reads asked in the same moment go as one call (batchedReads.ts). */
installBatchedReads();
/** #1917: before any read, so an identical read already out is shared (sharedReads.ts). */
installSharedReads();
/** #3379: over the join and under the phone store, so the first screen's reads are answered from the one launch request
 *  and the store keeps those answers as it keeps any (launchReads.ts). */
if (__GENIE_NMO__) installLaunchReads(lastAgent());
/** #3300: over the join, so a read whose finished answer is still fresh is answered on the phone (phoneStore.ts). */
installPhoneStore();
/** #3436: over every read layer, so the load times sheet sees each read as the screen does (loadTimes.ts). */
installLoadTimes();

/**
 * (#1540) The two previews below are only for an address that asks for one (`?firstrun=preview` or `=delegation`).
 * Their files used to be fetched on every open just to read that word, two rounds of files before the first read and
 * two more before the app; now they are fetched only when the word is there.
 */
const previewAsked = (): boolean => {
  try { return new URLSearchParams(location.search).has("firstrun"); } catch { return false; }
};

/** #1774: whether the signed-in person is an admin. Any failure is no. */
async function isAdmin(): Promise<boolean> {
  try {
    const token = sessionStorage.getItem("genie_token") ?? "";
    const res = await fetch("/api/me", { credentials: "same-origin", headers: token ? { Authorization: "Bearer " + token } : {} });
    return res.ok && ((await res.json()) as { isAdmin?: boolean }).isAdmin === true;
  } catch {
    return false;
  }
}

(async () => {
  // Say that the app's own script is running (#689). The page's inline safety net cannot
  // otherwise tell "the file holding the app never arrived" from "the app is still loading",
  // and it has to be able to: in the first case nothing of ours is running, so that net is the
  // only thing left that can wait for the server and try again.
  document.documentElement.classList.add("is-app-running");
  /** #3436: when, on the page's clock, for the load times sheet's "Opening the app" (loadTimes.ts). */
  document.documentElement.dataset.appRanAt = String(Math.round(performance.now()));

  // Install the global stale-chunk safety net FIRST — so even the initial import of
  // main.ts failing on a chunk the new build deleted triggers a recovery reload instead
  // of a blank, dead page.
  installStaleChunkRecovery();
  // (#2075) …and ask at once which build the server serves: a page that started behind it switches while the start-up
  // cover is still up, instead of waiting for the person to leave and come back.
  checkAtStart();

  // And watch for the server going away (#672). Installed here, before anything else wraps
  // fetch, so every read the app ever makes passes through it: the first read that cannot
  // reach the server at all puts up the calm "the app is restarting — your screen will come
  // back on its own" banner and takes it down again the moment the server answers.
  watchForTheServerGoingAway();

  // A link somebody shared to a trader (?trader=G-XXXXXXXX, #358) is kept for this tab before the sign-in gate below, so
  // a receiver who signs in first still lands on that trader.
  stashSharedTrader();

  // Catch Chrome's one-shot beforeinstallprompt before any await can miss it (it can
  // fire very early). Tiny zero-import module; the install sheet UI stays in main's
  // graph (install.ts) and reads the captured event from there.
  captureInstallPrompt();

  /**
   * magic.af/<name> (#773): the server wrote that name's card into the page. It is a public page,
   * signed in or not, so it goes up before every gate below and the app itself never starts.
   */
  /**
   * (#1621) The Chinese words are a file of their own, asked for here only when the screen is in Chinese, and waited for
   * (at most WORDS_WAIT_MS: past that the screen draws in English rather than not at all) before anything is drawn.
   */
  registerWordsLoader(loadPluginWords);
  const WORDS_WAIT_MS = 4000;
  const wordsReady = (): Promise<void> => Promise.race([ensureLangWords(), new Promise<void>((r) => setTimeout(r, WORDS_WAIT_MS))]);
  void ensureLangWords();

  /**
   * ═══ #1774/#1790: ON A COMPUTER THE APP IS THE LANDING FOR NOW (phoneGate.ts) ═══
   *
   * Everybody, signed in or not, on any address (magic.af/<name>, an invite, a coin link), gets the landing; Get started
   * raises the phone card, whose code carries this address. No app screen draws and nothing is read. Only
   * magic.af/admin goes on, and only for an admin (below).
   * #1794: on a local server (4536, private copies) a computer gets the desktop app as before; the server says which.
   */
  const computer = __GENIE_NMO__ && (await desktopClosed());
  if (computer && !adminAsked()) {
    await wordsReady();
    applyLangAttr();
    await showComputerLanding();
    document.documentElement.classList.add("is-splash-skip");
    return;
  }

  const nameCardTag = document.getElementById("genie-name-card");
  if (nameCardTag) {
    try {
      const { readNameCard, showNamePage } = await import("./namePage.ts");
      const card = readNameCard(JSON.parse(nameCardTag.textContent ?? "null"));
      if (card) {
        await wordsReady();
        applyLangAttr();
        document.documentElement.classList.add("is-splash-skip");
        await showNamePage(card);
        return;
      }
    } catch {
      /* the page could not be drawn: fall through to the app as if the address were "/" */
    }
  }

  /**
   * ═══ WATCHING THE FIRST RUN ON AN ACCOUNT THAT ALREADY HAS ONE ═══ (#756)
   *
   * `?firstrun=preview` plays the forge and the reveal and then carries on booting normally.
   * It creates nothing and asks the server for nothing — see `runFirstRunPreview`. Off unless
   * that word is in the address, and loaded on demand, so a normal boot neither runs it nor
   * carries it. It is here, before the gates, because the screens it plays belong before them.
   */
  if (previewAsked()) try {
    const { firstRunPreviewAsked, runFirstRunPreview } = await import("./firstRun.ts");
    if (firstRunPreviewAsked()) await runFirstRunPreview();
    /** #2353: `?firstrun=name` plays the hand-over and the name screen; nothing is asked or saved. */
    const { nameStepPreviewAsked, runNameStepPreview } = await import("./nameAgent.ts");
    if (nameStepPreviewAsked()) await runNameStepPreview();
    /** #2355: `?firstrun=install&device=ios|ios26|chrome|android|inapp` draws one install screen to look at. */
    const { installStepPreviewAsked, runInstallStepPreview } = await import("./installStep.ts");
    const device = installStepPreviewAsked();
    if (device) {
      document.documentElement.classList.add("is-splash-skip");
      await runInstallStepPreview(device);
    }
    /** #2392: `?firstrun=home` draws 1b, "Open me from your home screen", to look at. */
    const { homeScreenPreviewAsked, runHomeScreenStep } = await import("./homeScreenStep.ts");
    if (homeScreenPreviewAsked()) {
      document.documentElement.classList.add("is-splash-skip");
      await runHomeScreenStep();
    }
    /** #2354: `?firstrun=unlock` draws the unlock screen to look at. */
    const { unlockPreviewAsked, runUnlockPreview } = await import("./unlockStep.ts");
    if (unlockPreviewAsked()) {
      document.documentElement.classList.add("is-splash-skip");
      await runUnlockPreview();
    }
  } catch {
    /* the preview could not load — never hold up a real boot for it */
  }

  // Reflect the cached language on <html lang> before the gates render.
  applyLangAttr();

  /**
   * ═══ A DEVICE SIGNED IN BEFORE ASKS ITS FIRST READS NOW, NOT AFTER THE GATES (#916) ═══
   *
   * The gates below are reads in a row: who am I, then which agents, then the language, and only then the app and
   * the first screen's read. The agent list (on every open since #1540) and, on a device that has opened an agent
   * before, that screen's read are asked right here, side by side with "who am I". Both are guesses the gates check: a
   * list that did not answer 2xx is asked again, and a screen's read made for another agent than the one chosen is
   * dropped.
   */
  captureToken();
  const guess = lastAgent();
  /** #1774: on a computer (only /admin gets here) nothing is read before the admin check below. */
  if (!computer) {
    askAgentsEarly();
    /** #2318: opened at a coin's or a trader's address, the home screen's first reads wait for that page (linkFirst.ts);
     *  its screens ask them once it is full. */
    if (linkPageFirst()) { /* nothing early */ }
    else if (guess !== null) startEarlyReads(guess);
    /**
     * (#1540) A device's first open remembers no agent, so the first screen's read used to wait for "who am I", the agent
     * list and the whole app. For you's deck is the person's, whichever agent is chosen, so it goes out now as well.
     */
    else if (__GENIE_NMO__) startFirstOpenRead(`/api/nmo/for-you?lang=${getLang()}`);
  }

  // Post-purchase reload: a buy reloads into the new agent. Bring the "Setting up your
  // agent" loader up RIGHT NOW — before the async auth + agent-selection gates below —
  // so a successful buy goes straight to the loader with no blank/tasks flash in
  // between. main.ts's first `ready` frame calls finishWelcome() to dismiss it + fire
  // confetti. (index.html already hid the shell via .is-welcoming before first paint;
  // clear it now that the overlay covers everything.)
  let welcoming = false;
  try {
    const flag = sessionStorage.getItem("genie_welcome");
    if (flag) {
      sessionStorage.removeItem("genie_welcome");
      welcoming = true;
      // "switch" = changing agents (no confetti); anything else = post-purchase.
      showWelcome({ mode: flag === "switch" ? "switch" : "purchase" });
    }
  } catch {
    /* sessionStorage unavailable — just skip the welcome screen */
  }
  document.documentElement.classList.remove("is-welcoming");

  /**
   * ═══ #1657: A DEVICE THAT OPENED BEFORE STARTS ON ITS LAST STATE (openState.ts) ═══
   *
   * Signed in as last time, on the agent opened last time, in the language on this device: the app starts now and the
   * screens draw their last copies; the three reads go out in the background, and an answer that disagrees drops what
   * was kept and starts again the slow way. Not for a welcome after a purchase, an agent named in the address, or a
   * preview; those take the gates as before.
   */
  /** #1724: the splash on a return, from the page's start: at least this long, at most this long. */
  /** #2568: at least 0.8 s (the task's "never flashes"); the ending then waits for the first screen's facts (startSplash.ts). */
  const SPLASH_LEAST_MS = 800, SPLASH_MOST_MS = 1_500;
  const lastOpen = !welcoming && !previewAsked() && !new URLSearchParams(location.search).has("agent") ? readOpenState() : null;
  const fast = !computer && lastOpen !== null && lastOpen.agent === guess;
  let agentId: string | null;
  let langRead: Promise<void> = Promise.resolve();
  if (fast) {
    assumeSignedIn();
    try { sessionStorage.setItem("genie_agent", lastOpen.agent); } catch { /* private mode: the copies are not drawn, nothing else changes */ }
    agentId = lastOpen.agent;
    document.documentElement.classList.remove("is-booting");
    /** The splash is loaded the moment the first card is drawn (from its phone copy too): the person is looking at their
     *  last state, not waiting for the app. Anything else ends it as before.
     *  #1724 (owner, 5 Oct: the splash "buys time"): never sooner than SPLASH_LEAST_MS, so it never flashes, and never
     *  later than SPLASH_MOST_MS, so it never waits on the network. #2410: then the splash plays its outro over the card
     *  and, as it fades, says "genie:splash-ending" itself (What happened may take over the screen from it). */
    splashAt(0.45);
    /** #2568: an open from the last state holds the splash's ending for the first screen's facts from the page's start. */
    if (__GENIE_NMO__) holdSplashForFacts(0);
    let splashEnded = false;
    const endSplash = (): void => {
      if (splashEnded) return;
      splashEnded = true;
      splashLoaded();
    };
    window.addEventListener("genie:first-card-drawn", () => {
      window.setTimeout(endSplash, Math.max(0, SPLASH_LEAST_MS - performance.now()));
    }, { once: true });
    window.setTimeout(endSplash, Math.max(0, SPLASH_MOST_MS - performance.now()));
    seedUserLangDefault();
    reportUserZone();
    void hydrateLangFromServer();
    const slowWay = (): void => { void forgetKeptAnswers().finally(() => location.reload()); };
    void stillSignedIn().then((ok) => { if (!ok) slowWay(); });
    void agentStillOurs(lastOpen.agent).then((ours) => {
      if (ours === false) slowWay();
      else if (ours === true) writeOpenState(lastOpen.agent);
    });
  } else {
    await wordsReady();
    const authed = await ensureAuthenticated();
    if (authed) splashAt(0.45);
    /** #3330: no network, not signed out: the launch screen stays and says it cannot reach the network. */
    if (!authed && startWasUnreachable()) return;
    if (!authed) {
      /** #1657: signed out, nothing kept may stay (a read still in flight at sign-out can land after the store went). */
      void forgetKeptAnswers();
      if (welcoming) finishWelcome(false); // never trap the login screen behind the loader
      // Sign-in / landing is on screen now — drop the boot splash INSTANTLY (skip the
      // celebration outro; "ready to go" over a login screen would be a lie).
      document.documentElement.classList.add("is-splash-skip");
      return; // stay hidden: the sign-in screen (or its overlay) is what's on screen now
    }
    /** #1774: magic.af/admin on a computer: an admin goes on to the admin screen (main.ts), anybody else gets the landing. */
    if (computer && !(await isAdmin())) {
      await showComputerLanding();
      document.documentElement.classList.add("is-splash-skip");
      return;
    }

    // Auth confirmed — reveal the app shell that index.html hid before first paint. Done
    // here (not after main loads) so the static greeting/composer appear immediately; any
    // overlay still up (welcome loader, agent picker) keeps covering until it's dismissed.
    document.documentElement.classList.remove("is-booting");

    // Seed the owner's language default (browser-detected) from THIS point — authed but before
    // the no-agent gate below — so a brand-new user who hasn't bought an agent yet still lands
    // their browser language server-side. Their first agent (reply language baked at creation)
    // then greets in the right language. Set-if-absent, so it never overrides a real choice.
    seedUserLangDefault();
    // And which clock this reader is on (R-GEO). Same point in boot, same reason: authed, and
    // before anything renders a date, so the first server-rendered timestamp they see is already
    // in their own zone rather than the server's.
    reportUserZone();
    // The language read needs no agent, so it goes out alongside the agent list rather than after it (#916).
    langRead = hydrateLangFromServer();

    agentId = await ensureAgentSelected();
    if (!agentId && startWasUnreachable()) return;
    if (!agentId) {
      if (welcoming) finishWelcome(false);
      // The agent picker / no-agent gate is on screen — drop the boot splash instantly.
      document.documentElement.classList.add("is-splash-skip");
      return;
    }
    /** #1657: the next open may start on this one's state, once the agent's name is picked. */
    if (chosenIsNamed) writeOpenState(agentId);
    splashAt(0.6);
  }

  installAgentHeader(agentId);
  rememberLastAgent(agentId);
  /**
   * #2392: the home-screen icon and name a phone installs from here on are this agent's own. The first run's install
   * (#2402) comes before the sign-in, so it is always the app's own magic.af; this is for a later install from the
   * every-visit sheet, and for Android's own later check of an installed app's install file.
   */
  pointHomeIconAt(agentId);
  /** (#907) The reads a screen opens with, asked now rather than after the whole app has loaded (earlyReads.ts). */
  if (!linkPageFirst()) startEarlyReads();

  /**
   * `?firstrun=delegation` shows the delegation screen (#758) over the chosen agent and then boots
   * as usual. Off unless typed; loaded on demand. Allow on it is the real Allow, which only reads
   * and records nothing unless the wallet rules are on.
   */
  if (previewAsked()) try {
    const { delegationPreviewAsked, runDelegationPreview } = await import("./delegation.ts");
    if (delegationPreviewAsked()) {
      if (welcoming) finishWelcome(false);
      await runDelegationPreview(sessionStorage.getItem("genie_token") ?? "", agentId);
    }
  } catch {
    /* the preview could not load — never hold up a real boot for it */
  }

  /**
   * #2356: a new person's first run goes on here when it is due (firstRunSequence.ts): the unlock, then the app opens
   * with the swipe lesson in front (#2402: the install came before the sign-in). Only on this device, only for the
   * agent it was started for. Nothing due: Get started's mark goes, so a later signed-out open is the landing.
   */
  if (await firstRunDueHere(agentId)) {
    if (welcoming) finishWelcome(false);
    document.documentElement.classList.add("is-splash-skip");
    await runFirstRunBeforeApp(agentId);
  } else clearDoor();

  /**
   * #3423: the agent's machine is NOT warmed at every open any more. This call used to wake it here, and the chat's
   * connection woke it again, about 6 minutes of machine time per open whether or not the person chatted. It wakes on
   * the first sign of chat now (main.ts `wakeAgent`). Only a switch or a purchase, whose loader waits for the agent,
   * still asks at once.
   */
  if (welcoming) {
    try {
      const tok = sessionStorage.getItem("genie_token");
      void fetch(`/api/agents/${encodeURIComponent(agentId)}/warm`, {
        method: "POST",
        credentials: "same-origin",
        headers: tok ? { Authorization: "Bearer " + tok } : {},
      }).catch(() => {});
    } catch {
      /* sessionStorage blocked / fetch unavailable — the connection asks once it opens */
    }
  }

  // Adopt the server-stored language preference before the app renders, so a
  // fresh device (or one changed elsewhere) paints in the right language with no
  // reload. HARD-BOUNDED: nothing between auth and the main.ts import may hold
  // the app hostage — a slow read means we boot with the cached/browser language
  // and the durable one applies on the next load.
  if (!fast) await Promise.race([langRead, new Promise<void>((r) => setTimeout(r, 2000))]);
  // The app's own main file is the last thing fetched before the app appears (#689). Unlike the
  // two reads above it cannot simply be asked for again: the browser remembers a refused file for
  // the life of the page and hands the same refusal back without going near the network (measured
  // on #672). So the failure is avoided rather than recovered — if the app already knows the
  // server is away, wait for it first. Asking costs nothing when the server is fine.
  /** #1657: with no network at all the kept build files start the app (sw.js); waiting would never end. */
  if (serverIsKnownAway() && navigator.onLine !== false) await waitForServer();
  /* the language may have turned Chinese just now (the server's choice) */
  await wordsReady();
  await import("./main.ts");
  /** #2410: the app's files are in; the first screen's data is the last step (main.ts markAppReady, or the first card). */
  splashAt(0.85);
  /** #2568: any other open holds the splash's ending for the first screen's facts from here (a no-op after the above). */
  if (__GENIE_NMO__) holdSplashForFacts(performance.now());
  clearStaleReloadGuard(); // booted cleanly on the current build — re-arm the reload guard
  // …and re-arm the page's own one-retry-per-tab guard (#689), so the next restart this tab
  // lives through can be recovered from too.
  try {
    sessionStorage.removeItem("genie_boot_retry");
  } catch {
    /* sessionStorage blocked — the guard simply was not there to re-arm */
  }
  startVersionWatcher(); // poll for newer deploys and auto-refresh everyone onto the new build
  // (#916) Keep the frame for an open with no network (src/web/sw.js), once start-up has settled.
  if ("serviceWorker" in navigator) {
    window.setTimeout(() => {
      void navigator.serviceWorker.register("/sw.js").then(() => navigator.serviceWorker.ready).then((reg) => {
        // The build files this page is running, so an open with no network can start the app, not only its frame.
        const keep = performance.getEntriesByType("resource").map((e) => e.name).filter((u) => u.startsWith(location.origin) && /\.(js|css)$/.test(u));
        /** #3301: and the list of every file this build can load (chart code first), all kept on the phone behind it. */
        const files = document.querySelector<HTMLMetaElement>('meta[name="genie-files"]')?.content;
        reg.active?.postMessage({ keep, ...(files ? { files } : {}) });
      }).catch(() => undefined);
    }, 5000);
  }
  await installAgentCard(agentId);
})();
